import { useState, useEffect, useCallback, useRef } from 'react';
import { icCidService } from '../services/icCids/ICcIdService';
import { ICcIdResponse, ICcIdFilters } from '../types/dtos';
import { ER2_SP_NEW_LOT_FROM, resolveRjMgEsFastchipIccidSearch } from '../utils/fastchipClusterRules';
import { extractApiErrorMessage, isCanceledError } from '../services/utils/apiErrorUtils';

/** Tamanho padrão do pool de ICCIDs para modais de fastchip (carrega mais sob demanda). */
export const FASTCHIP_ICCID_POOL_PAGE_SIZE = 60;

interface UseICcIdsOptions {
  estado?: string;
  /** Cluster Senior (ex.: DH) — necessário no mercado RJ/MG/ES para não perder stock na 1ª página. */
  cluster?: string;
  createdFrom?: Date;
  disponivel?: boolean;
  autoLoad?: boolean;
  compact?: boolean;
}

interface UseICcIdsReturn {
  iccids: ICcIdResponse[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  refetch: (signal?: AbortSignal) => Promise<void>;
  loadMore: () => Promise<void>;
  searchICcIds: (filters: ICcIdFilters, signal?: AbortSignal) => Promise<void>;
}

export const useICcIds = (options: UseICcIdsOptions = {}): UseICcIdsReturn => {
  const { estado, cluster, createdFrom, disponivel = true, autoLoad = true, compact = false } = options;

  const [iccids, setIccids] = useState<ICcIdResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const activeFiltersRef = useRef<ICcIdFilters>({});

  const applySearchResult = useCallback(
    (items: ICcIdResponse[], append: boolean, nextPage: number, nextHasMore: boolean) => {
      setIccids((prev) => (append ? [...prev, ...items] : items));
      setPage(nextPage);
      setHasMore(nextHasMore);
    },
    []
  );

  const searchICcIds = useCallback(
    async (filters: ICcIdFilters, signal?: AbortSignal) => {
      activeFiltersRef.current = filters;
      setLoading(true);
      setError(null);

      try {
        const result = await icCidService.searchICcIds(
          filters,
          1,
          FASTCHIP_ICCID_POOL_PAGE_SIZE,
          undefined,
          false,
          { signal, compact }
        );
        if (signal?.aborted) return;
        applySearchResult(result.items, false, 1, result.hasNextPage);
      } catch (err: unknown) {
        if (signal?.aborted || isCanceledError(err)) return;
        setError(extractApiErrorMessage(err, 'Erro ao buscar ICCIDs'));
        setIccids([]);
        setHasMore(false);
        setPage(1);
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [applySearchResult, compact]
  );

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    const nextPage = page + 1;
    setLoading(true);
    setError(null);

    try {
      const result = await icCidService.searchICcIds(
        activeFiltersRef.current,
        nextPage,
        FASTCHIP_ICCID_POOL_PAGE_SIZE,
        undefined,
        false,
        { compact }
      );
      applySearchResult(result.items, true, nextPage, result.hasNextPage);
    } catch (err: unknown) {
      if (isCanceledError(err)) return;
      setError(extractApiErrorMessage(err, 'Erro ao buscar ICCIDs'));
    } finally {
      setLoading(false);
    }
  }, [applySearchResult, compact, hasMore, loading, page]);

  const refetch = useCallback(
    async (signal?: AbortSignal) => {
      const filters: ICcIdFilters = { disponivel };
      if (estado) filters.estado = estado;
      if (cluster) filters.cluster = cluster;
      if (createdFrom) filters.createdFrom = createdFrom;
      await searchICcIds(filters, signal);
    },
    [estado, cluster, createdFrom, disponivel, searchICcIds]
  );

  useEffect(() => {
    if (!autoLoad || !estado) return;
    // Aborta a busca anterior ao trocar estado/cluster para evitar que uma resposta
    // obsoleta (fora de ordem) sobrescreva o resultado da busca mais recente.
    const abortController = new AbortController();
    void refetch(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [estado, cluster, autoLoad, refetch]);

  return {
    iccids,
    loading,
    error,
    hasMore,
    refetch,
    loadMore,
    searchICcIds,
  };
};

/**
 * Cliente RJ/MG/ES: âmbito geográfico multi-UF (`RJ,MG,ES`).
 * Com `DH_FASTCHIP_ENABLED=true` a API também recebe `cluster=DH` (há ICCIDs regionais
 * com cluster NULL nas mesmas UFs). Com flag false → pool E.R.2 sem cluster DH.
 */
const resolveEstadoAndClusterForSearch = (estado: string | undefined | null) => {
  const rjMgEs = resolveRjMgEsFastchipIccidSearch(estado);
  if (rjMgEs) {
    return {
      estado: rjMgEs.estadoFilter,
      cluster: rjMgEs.cluster,
      createdFrom: rjMgEs.usesDhCluster ? undefined : ER2_SP_NEW_LOT_FROM,
    };
  }
  const normalized = (estado ?? '').trim().toUpperCase();
  return {
    estado: normalized,
    cluster: undefined as string | undefined,
    createdFrom: normalized === 'SP' ? ER2_SP_NEW_LOT_FROM : undefined,
  };
};

// Hook específico para ICCIDs disponíveis por estado
export const useAvailableICcIdsByState = (
  estado: string,
  options?: { compact?: boolean; autoLoad?: boolean }
) => {
  const { estado: estadoParaBuscar, cluster, createdFrom } = resolveEstadoAndClusterForSearch(estado);

  return useICcIds({
    estado: estadoParaBuscar,
    cluster,
    createdFrom,
    disponivel: true,
    autoLoad: options?.autoLoad ?? true,
    compact: options?.compact ?? true,
  });
};

/**
 * Pool de ICCIDs para troca FAST: com `referenceCcId` usa GET /icCids/search (filtro regional do chip de referência);
 * sem referência: UF do cliente; RJ/MG/ES usa pool flag-driven (E.R.2 ou DH).
 */
export const useAvailableICcIdsForFastchipSwap = (
  referenceIccid: string | undefined | null,
  clientEstado: string
) => {
  const [iccids, setIccids] = useState<ICcIdResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const requestSequenceRef = useRef(0);
  const refKey = referenceIccid?.trim() || '';
  const { estado: estadoParaBuscar, cluster: rjMgEsCluster, createdFrom: er2LotFrom } =
    resolveEstadoAndClusterForSearch(clientEstado);
  const activeFiltersRef = useRef<ICcIdFilters>({});

  const fetchPage = useCallback(
    async (pageNumber: number, append: boolean, signal: AbortSignal, isCurrentRequest: () => boolean) => {
      const result = await icCidService.searchICcIds(
        activeFiltersRef.current,
        pageNumber,
        FASTCHIP_ICCID_POOL_PAGE_SIZE,
        undefined,
        false,
        { signal, compact: true }
      );
      if (signal.aborted || !isCurrentRequest()) return;
      setIccids((prev) => (append ? [...prev, ...result.items] : result.items));
      setPage(pageNumber);
      setHasMore(result.hasNextPage);
    },
    []
  );

  useEffect(() => {
    const ac = new AbortController();
    const requestId = ++requestSequenceRef.current;
    const isCurrentRequest = () => requestId === requestSequenceRef.current;
    setIccids([]);
    setHasMore(false);
    setPage(1);

    const run = async () => {
      setLoading(true);
      try {
        if (refKey) {
          activeFiltersRef.current = { disponivel: true, referenceCcId: refKey };
          await fetchPage(1, false, ac.signal, isCurrentRequest);
        } else if (estadoParaBuscar) {
          activeFiltersRef.current = {
            disponivel: true,
            estado: estadoParaBuscar,
            ...(rjMgEsCluster ? { cluster: rjMgEsCluster } : {}),
            ...(er2LotFrom ? { createdFrom: er2LotFrom } : {}),
          };
          await fetchPage(1, false, ac.signal, isCurrentRequest);
        } else if (!ac.signal.aborted && isCurrentRequest()) {
          setIccids([]);
        }
      } catch {
        if (!ac.signal.aborted && isCurrentRequest()) {
          setIccids([]);
          setHasMore(false);
        }
      } finally {
        if (isCurrentRequest()) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => ac.abort();
  }, [refKey, estadoParaBuscar, rjMgEsCluster, er2LotFrom, fetchPage]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      await fetchPage(page + 1, true, new AbortController().signal, () => true);
    } catch {
      // mantém pool atual em falha transitória
    } finally {
      setLoading(false);
    }
  }, [fetchPage, hasMore, loading, page]);

  return { iccids, loading, hasMore, loadMore };
};

// Hook para buscar ICCIDs com filtros customizados
export const useICcIdsWithFilters = () => {
  return useICcIds({ autoLoad: false });
};
